import { AxiosResponse } from "axios";
import ApiResponse from "../Models/ApiResponse";
import { Page } from "../Models/Page";
import ReferenceModel from "../Models/ReferenceModel";
import { INotificationRecipient } from "../Models/NotificationRecipientModel";
import api from "../Interceptors/Interceptor";
import { BaseService } from "./BaseService";
import { IWorkflowSpotlight } from "./ProcessflowService";

export interface IObjectiveScope {
    metricViewUuid?: string;
    dataType?: string;
    operator?: string;
    fieldName?: string;
    fieldValue?: string;
}

export interface IObjectiveTarget {
    uuid?: string;
    label?: string;
    deleted?: boolean;
    targetValue?: number;
    targetDirection?: string;
    autoCompleteOnPeriodPass?: boolean;
    status?: string;
    orderIndex?: number;
    periodStartDate?: string;
    periodEndDate?: string;
    toleranceMode?: string;
    toleranceValue?: number;
    progressPct?: number;
    evaluationCriteria?: IObjectiveEvaluationCriteria;
}

export interface IObjectiveEvaluationCriteriaCondition {
    parameter?: string;
    sqlOperator?: string;
    values?: string[];
    value?: string;
    dataType?: string;
}

export interface IObjectiveEvaluationCriteria {
    uuid?: string;
    metricView?: ReferenceModel;
    metricDatasetName?: string;
    metricTableName?: string;
    currentValue?: number;
    datapoint?: string;
    functionName?: string;
    datapointExpression?: string;
    evaluationPeriod?: ReferenceModel;
    evaluationFrequency?: ReferenceModel;
    conditions?: IObjectiveEvaluationCriteriaCondition[];
    latestResultExecutedAt?: string;
    latestResultStatus?: string;
    latestResultContext?: string;
}

export interface IObjectiveModel {
    uuid: string;
    customer?: ReferenceModel;
    title: string;
    description?: string;
    domain?: string;
    status?: string;
    severity?: string;
    targetYear?: number;
    periodStartDate?: string;
    periodEndDate?: string;
    targetMode?: string;
    fixedTargetValue?: number;
    toleranceMode?: string;
    toleranceValue?: number;
    linkHash?: string;
    requiredPermission?: string;
    totalTargets?: number;
    onTrackTargets?: number;
    worstTargetStatus?: string;
    targetStatusCounts?: Record<string, number>;
    progressPct?: number;
    createdAt?: string;
    updatedAt?: string;
    active?: boolean;
    scopes?: IObjectiveScope[];
    targets?: IObjectiveTarget[];
    roles?: ReferenceModel[];
    notificationRecipients?: INotificationRecipient[];
    recipientsInternal?: string[];
    recipientsExternal?: string[];
    notifyInApp?: boolean;
}

export interface IObjectiveCrudModel {
    customer?: ReferenceModel;
    title: string;
    description?: string;
    domain?: string;
    status?: string;
    severity?: string;
    targetYear?: number;
    periodStartDate?: string;
    periodEndDate?: string;
    targetMode?: string;
    fixedTargetValue?: number;
    toleranceMode?: string;
    toleranceValue?: number;
    linkHash?: string;
    active?: boolean;
    scopes?: IObjectiveScope[];
    targets?: IObjectiveTargetCrud[];
    roles?: ReferenceModel[];
    notificationRecipients?: INotificationRecipient[];
    recipientsInternal?: string[];
    recipientsExternal?: string[];
    notifyInApp?: boolean;
}

export interface IObjectiveTargetCrud extends IObjectiveTarget {
    evaluationCriteria?: {
        metricViewUuid?: string;
        datapoint?: string;
        functionName?: string;
        datapointExpression?: string;
        datapointBuilderStateJson?: string;
        datapointConfigurationMode?: string;
        evaluationPeriodCode?: string;
        evaluationFrequencyCode?: string;
        conditions?: IObjectiveEvaluationCriteriaCondition[];
    };
}

export interface IObjectiveTask {
    uuid: string;
    objectiveId?: string;
    objectiveTargetUuid?: string;
    title: string;
    description?: string;
    status?: string;
    priority?: string;
    createdAt?: string;
    createdBy?: ReferenceModel;
    assignedUser?: ReferenceModel;
    deadline?: string;
}

export interface IObjectiveEvent {
    uuid: string;
    objectiveId?: string;
    eventType?: string;
    contentTypeIcon?: string;
    contentTypeDescription?: string;
    payload?: Record<string, unknown>;
    actorName?: string;
    createdBy?: string;
    createdAt?: string;
}

export interface IObjectiveHistoricalContext {
    currentValue?: number;
    previousPeriodValue?: number;
    samePeriodLastYearValue?: number;
    yearAverageValue?: number;
    targetYear?: number;
}

export interface IObjectiveCriteriaPreviewPayload {
    metricUuid?: string;
    metricViewUuid?: string;
    datapoint?: string;
    functionName?: string;
    datapointExpression?: string;
    datapointBuilderStateJson?: string;
    datapointConfigurationMode?: string;
    evaluationPeriod?: string;
    conditions?: IObjectiveEvaluationCriteriaCondition[];
    periodStartDate?: string;
    periodEndDate?: string;
    referencePeriodStartDate?: string;
    referencePeriodEndDate?: string;
}

export interface IObjectiveCriteriaPreviewResult {
    value?: number;
    sampleSize?: number;
    evaluationPeriod?: string;
}

export interface IObjectiveCriteriaExecutionQueryResult {
    query?: string;
    previousPeriodQuery?: string;
}

export interface IObjectiveCriteriaHistoryItem {
    uuid: string;
    evaluationCriteriaUuid?: string;
    executedAt?: string;
    value?: number;
    currentValue?: number;
    sampleSize?: number;
    resultStatus?: string;
    resultContext?: string;
}

export interface IHomeObjective {
    onTrackCount: number;
    flowUuids: string[];
}

export class ObjectiveService extends BaseService<IObjectiveModel, IObjectiveCrudModel> {
    protected modelUrl(): string {
        return "objective";
    }

    public async fetchItem(uuid: string): Promise<AxiosResponse<ApiResponse<IObjectiveModel>>> {
        return api.get(`v1/objective/${uuid}`);
    }

    public async fetchSpotlight(params?: { customer?: string }): Promise<AxiosResponse<ApiResponse<IWorkflowSpotlight>>> {
        return api.get("v1/objective/spotlight", { params });
    }

    public async fetchHome(params?: { customer?: string }): Promise<AxiosResponse<ApiResponse<IHomeObjective>>> {
        return api.get("v1/objective/home", { params });
    }

    async fetchDomains(): Promise<AxiosResponse<ApiResponse<string[]>>> {
        return api.get("v1/objective/domains");
    }

    async fetchTasks(uuid: string): Promise<AxiosResponse<ApiResponse<IObjectiveTask[]>>> {
        return api.get(`v1/objective/${uuid}/task`);
    }

    async createTask(uuid: string, payload: {
        objectiveTargetUuid?: string;
        title: string;
        description?: string;
        priority?: string;
        assignedUserUuid?: string;
    }): Promise<AxiosResponse<ApiResponse<IObjectiveTask>>> {
        return api.post(`v1/objective/${uuid}/task`, payload);
    }

    async fetchActivity(uuid: string): Promise<AxiosResponse<ApiResponse<IObjectiveEvent[]>>> {
        return api.get(`v1/objective/${uuid}/activity`);
    }

    async postComment(uuid: string, text: string): Promise<AxiosResponse<ApiResponse<IObjectiveEvent>>> {
        return api.post(`v1/objective/${uuid}/comment`, { text });
    }

    async deleteComment(uuid: string, commentUuid: string): Promise<AxiosResponse<ApiResponse<boolean>>> {
        return api.delete(`v1/objective/${uuid}/comment/${commentUuid}`);
    }

    async updateTask(
        taskUuid: string,
        payload: Partial<Pick<IObjectiveTask, "title" | "description" | "status" | "priority">>,
    ): Promise<AxiosResponse<ApiResponse<IObjectiveTask>>> {
        return api.put(`v1/objective/task/${taskUuid}`, payload);
    }

    async assignTaskUser(taskUuid: string, assignedUserUuid: string | null): Promise<AxiosResponse<ApiResponse<IObjectiveTask>>> {
        return api.put(`v1/objective/task/${taskUuid}/assignee`, { assignedUserUuid });
    }

    async fetchTargetCriteriaHistory(targetUuid: string, limit = 30): Promise<AxiosResponse<ApiResponse<IObjectiveCriteriaHistoryItem[]>>> {
        return api.get(`v1/objective/target/${targetUuid}/criteria/history`, { params: { limit } });
    }

    async submitTargetCriteriaManualInput(
        targetUuid: string,
        payload: { value: number; note?: string },
    ): Promise<AxiosResponse<ApiResponse<boolean>>> {
        return api.post(`v1/objective/target/${targetUuid}/criteria/manual-input`, payload);
    }

    async addEmailEvent(uuid: string, to: string, subject: string, body?: string): Promise<AxiosResponse<ApiResponse<IObjectiveEvent>>> {
        return api.post(`v1/objective/${uuid}/email-event`, { to, subject, body });
    }

    async fetchHistoricalContext(uuid: string): Promise<AxiosResponse<ApiResponse<IObjectiveHistoricalContext>>> {
        return api.get(`v1/objective/${uuid}/historical-context`);
    }

    async refreshTargetCriteria(targetUuid: string): Promise<AxiosResponse<ApiResponse<boolean>>> {
        return api.post(`v1/objective/target/${targetUuid}/criteria/refresh`);
    }

    async updateObjectiveStatus(uuid: string, status: string): Promise<AxiosResponse<ApiResponse<IObjectiveModel>>> {
        return api.put(`v1/objective/${uuid}/status`, { status });
    }

    async updateTargetStatus(targetUuid: string, status: string): Promise<AxiosResponse<ApiResponse<IObjectiveTarget>>> {
        return api.put(`v1/objective/target/${targetUuid}/status`, { status });
    }

    async previewCriteria(
        payload: IObjectiveCriteriaPreviewPayload,
    ): Promise<AxiosResponse<ApiResponse<IObjectiveCriteriaPreviewResult>>> {
        return api.post("v1/objective/criteria/preview", payload);
    }

    async fetchCriteriaExecutionQuery(
        payload: IObjectiveCriteriaPreviewPayload,
    ): Promise<AxiosResponse<ApiResponse<IObjectiveCriteriaExecutionQueryResult>>> {
        return api.post("v1/objective/criteria-execution-query", payload);
    }
}
